import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";

/**
 * The four `VibeDeploymentStatus` values that consume platform resources
 * (build minutes, compute, traffic) and therefore qualify for the
 * cost-safety auto-rollback sweep. FAILED, ROLLED_BACK, SUPERSEDED,
 * DISPLACED and CANCELLED are terminal and explicitly skipped.
 *
 * DISPLACED is skipped for a stronger reason than the other three: a displaced
 * deployment never had an allocation at all — a newer deployment became the
 * app's rollout target before it could go live — so there is nothing for the
 * sweep to roll back and no traffic it holds. CANCELLED is skipped for the same
 * reason: its build was stopped before it produced an image, so it never had one.
 *
 * Single source of truth — consumed by:
 *
 *   - The rollback sweeper's read port (Prisma
 *     `where.status: { in: [...] }` filter): finds candidate
 *     deployments for SUSPENDED orgs.
 *   - The rollback sweeper's per-row composite TX: the status-guarded
 *     `updateMany WHERE id=$id AND status IN (...)` re-narrowing for
 *     race-loser detection.
 *   - The `DEPLOYMENT_ROLLED_BACK_COST_SAFETY` audit payload schema's
 *     `priorStatus` Zod literal — `VibeCostSafetyActiveStatusSchema`.
 *   - `VibeDeployment.isCostSafetyActive()` type-guard on the entity.
 *
 * The `satisfies` constraint pins each literal to a member of the
 * generated `VIBE_DEPLOYMENT_STATUS_VALUES` tuple. The
 * `_Uncategorized` exhaustiveness check below ensures that adding a
 * new `VibeDeploymentStatus` to the Prisma enum FORCES the developer
 * to classify it as either active (add to the tuple) or terminal
 * (add to the `Exclude` list in the check). Drift impossible.
 */
type VibeDeploymentStatusLiteral = (typeof DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES)[number];

export const VIBE_COST_SAFETY_ACTIVE_STATUSES = [
  "BUILDING",
  "AWAITING_APPROVAL",
  "DEPLOYING",
  "HEALTHY"
] as const satisfies readonly VibeDeploymentStatusLiteral[];

export type VibeCostSafetyActiveStatus = (typeof VIBE_COST_SAFETY_ACTIVE_STATUSES)[number];

export const VibeCostSafetyActiveStatusSchema = z.enum(VIBE_COST_SAFETY_ACTIVE_STATUSES);

/**
 * Compile-time exhaustiveness check. Every `VibeDeploymentStatus`
 * member must be EITHER in the active tuple above OR explicitly
 * listed as terminal here. Adding a new status to the Prisma enum
 * without categorizing it triggers a compile error on the
 * `_check` assignment below — the only ways to satisfy the never
 * constraint are (a) categorize the new status as active by adding
 * it to the tuple, or (b) categorize it as terminal by adding it
 * to the `Exclude` clause here.
 */
type _Uncategorized = Exclude<
  VibeDeploymentStatusLiteral,
  VibeCostSafetyActiveStatus | "FAILED" | "ROLLED_BACK" | "SUPERSEDED" | "DISPLACED" | "CANCELLED"
>;
const _check: _Uncategorized extends never ? true : never = true;
void _check;
